import React, { useState, useEffect } from 'react';
import { Globe, Sparkles, Loader2, CheckCircle, AlertCircle, ExternalLink, Eye, Lock, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import api from '../utils/api';
import { toast } from 'sonner';

const PREMIUM_PLANS = ['pro', 'enterprise'];

const VisionImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const [url, setUrl] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(''); // idle, capturing, analyzing, generating, complete, error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [planLevel, setPlanLevel] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchTenantPlan();
    }
  }, [isOpen]);

  const fetchTenantPlan = async () => {
    try {
      setLoadingPlan(true);
      const res = await api.get('/tenant');
      setPlanLevel(res.data.plan_level || 'starter');
    } catch (error) {
      console.error('Failed to fetch plan:', error);
      setPlanLevel('starter');
    } finally {
      setLoadingPlan(false);
    }
  };

  const isPremiumPlan = PREMIUM_PLANS.includes(planLevel);

  const statusMessages = {
    idle: '',
    capturing: 'Taking screenshot of website...',
    analyzing: 'AI is analyzing the page layout...',
    generating: 'Generating CMS blocks...',
    complete: 'Import complete!',
    error: 'Import failed'
  };

  const handleImport = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }

    // Validate URL
    try {
      new URL(url);
    } catch {
      toast.error('Please enter a valid URL (including https://)');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);
    setProgress(0);

    // Simulate progress stages
    setStatus('capturing');
    setProgress(20);

    try {
      // Start the import - simulate progress during API call
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev < 30) {
            setStatus('capturing');
            return prev + 2;
          } else if (prev < 60) {
            setStatus('analyzing');
            return prev + 1;
          } else if (prev < 85) {
            setStatus('generating');
            return prev + 0.5;
          }
          return prev;
        });
      }, 500);

      const response = await api.post('/templates/import-vision', {
        url: url,
        template_name: templateName || `Imported from ${new URL(url).hostname}`
      });

      clearInterval(progressInterval);
      setProgress(100);
      setStatus('complete');
      setResult(response.data);

      toast.success(`Successfully imported ${response.data.blocks?.length || 0} blocks!`);

    } catch (err) {
      setStatus('error');
      setProgress(0);
      const errorMessage = err.response?.data?.detail || err.message || 'Import failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const handleUseTemplate = () => {
    if (result) {
      onImportComplete(result);
      handleClose();
    }
  };

  const handleClose = () => {
    setUrl('');
    setTemplateName('');
    setImporting(false);
    setProgress(0);
    setStatus('idle');
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <span>Vision Import</span>
              <span className="ml-2 text-xs bg-gradient-to-r from-violet-500 to-purple-500 text-white px-2 py-0.5 rounded-full font-normal">
                AI-Powered
              </span>
            </div>
          </DialogTitle>
          <DialogDescription>
            Import any website using AI vision analysis. We&apos;ll screenshot the page, analyze the layout, and recreate it using CMS blocks.
          </DialogDescription>
        </DialogHeader>

        {/* Loading Plan State */}
        {loadingPlan ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-slate-400" size={32} />
          </div>
        ) : !isPremiumPlan ? (
          /* Plan Gating - Not Premium */
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="text-violet-600" size={40} />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2">Upgrade to Pro</h3>
            <p className="text-slate-600 max-w-md mb-6">
              Vision Import is a premium feature that uses AI to analyze screenshots of any website 
              and recreate its layout in your CMS. Upgrade your plan to access this powerful feature.
            </p>
            <div className="flex flex-col items-center gap-2 mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Crown className="text-amber-500" size={16} />
                Your current plan: <span className="font-semibold capitalize">{planLevel}</span>
              </div>
            </div>
            <Button 
              onClick={() => window.location.href = '/settings?tab=billing'}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
            >
              <Crown size={16} />
              Upgrade to Pro
            </Button>
          </div>
        ) : (
          /* Main Content - Premium Users */
          <>
            <div className="space-y-6 py-4">
          {/* URL Input */}
          {!result && (
            <>
              <div className="space-y-2">
                <Label htmlFor="url">Website URL</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="url"
                      type="url"
                      placeholder="https://example.com"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="pl-10"
                      disabled={importing}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(url, '_blank')}
                    disabled={!url}
                    title="Preview URL"
                  >
                    <ExternalLink size={18} />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Template Name (optional)</Label>
                <Input
                  id="name"
                  placeholder="My Imported Template"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  disabled={importing}
                />
              </div>

              {/* How it works */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-sm text-slate-700 mb-3">How Vision Import Works:</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="space-y-2">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-violet-600 font-bold">1</span>
                    </div>
                    <p className="text-xs text-slate-600">Screenshot capture of the entire page</p>
                  </div>
                  <div className="space-y-2">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-violet-600 font-bold">2</span>
                    </div>
                    <p className="text-xs text-slate-600">AI analyzes layout, sections & colors</p>
                  </div>
                  <div className="space-y-2">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-violet-600 font-bold">3</span>
                    </div>
                    <p className="text-xs text-slate-600">Generates editable CMS blocks</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Progress */}
          {importing && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Loader2 className="animate-spin text-violet-600" size={24} />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{statusMessages[status]}</p>
                  <p className="text-sm text-slate-500">This may take 30-60 seconds</p>
                </div>
              </div>
              <Progress value={progress} className="h-2" />
              <div className="text-center text-sm text-slate-500">
                {Math.round(progress)}% complete
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-red-800">Import Failed</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Import Successful!</p>
                  <p className="text-sm text-green-600 mt-1">
                    Created {result.blocks?.length || 0} blocks from {new URL(url).hostname}
                  </p>
                </div>
              </div>

              {/* Layout Notes */}
              {result.layout_notes && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-sm text-slate-700 mb-2">Layout Analysis:</h4>
                  <p className="text-sm text-slate-600">{result.layout_notes}</p>
                </div>
              )}

              {/* Block Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-sm text-slate-700 mb-3">Blocks Generated:</h4>
                <div className="flex flex-wrap gap-2">
                  {result.blocks?.reduce((acc, block) => {
                    const type = block.type;
                    const existing = acc.find(b => b.type === type);
                    if (existing) {
                      existing.count++;
                    } else {
                      acc.push({ type, count: 1 });
                    }
                    return acc;
                  }, []).map(({ type, count }) => (
                    <span key={type} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded border text-xs">
                      <span className="font-medium capitalize">{type}</span>
                      <span className="text-slate-400">×{count}</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Theme Colors */}
              {result.theme && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-sm text-slate-700 mb-3">Extracted Theme:</h4>
                  <div className="flex gap-3">
                    {Object.entries(result.theme).map(([key, color]) => (
                      <div key={key} className="text-center">
                        <div 
                          className="w-8 h-8 rounded-lg border shadow-sm mx-auto"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                        <span className="text-xs text-slate-500 mt-1 block">
                          {key.replace('Color', '')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Credits Info */}
              <div className="text-sm text-slate-500 text-center">
                <span className="font-medium">{result.credits_used}</span> credits used • 
                <span className="font-medium"> {result.credits_remaining}</span> credits remaining
              </div>
            </div>
          )}
            </div>

            {/* Credits Cost Alert - show when not importing and no result */}
            {!result && !importing && (
              <Alert className="bg-amber-50 border-amber-200">
                <Sparkles className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Vision import uses <strong>3 AI credits</strong> to capture, analyze, and convert the page layout.
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              {!result ? (
                <>
                  <Button variant="outline" onClick={handleClose} disabled={importing}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleImport} 
                    disabled={importing || !url}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={18} />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2" size={18} />
                        Start Vision Import (3 Credits)
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                  <Button 
                    onClick={handleUseTemplate}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                  >
                    <Eye className="mr-2" size={18} />
                    Use This Template
                  </Button>
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VisionImportModal;
